import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from './activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { ItemsContext_VariantsPackFieldsFragment } from '@/types';
import { KeyboardAwareRecyclerList, ListSection } from './recycler-list';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { QuantitySelector } from './quantity-selector';
import { ScannerVariantPackListItem } from './scanner-variant-pack-list-item';
import { SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { Text, View } from 'react-native';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';

interface Props {
  isSearching: Boolean;
  onAddItemToBatch: (
    variantPackUUID: string,
    quantity: number,
  ) => Promise<void>;
  quantityMap: QuantityMapType;
  sections: ListSection<ItemsContext_VariantsPackFieldsFragment>[];
}

export const TagPrintBatchItemSelection: React.FC<Props> = ({
  isSearching,
  onAddItemToBatch,
  quantityMap,
  sections,
}) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const [styles] = useStyles();

  return isSearching ? (
    <View style={[styles.searchLoading]}>
      <ActivityIndicator />
      <Text style={[TYPOGRAPHY.p, styles.searchText]}>
        {i18n.t('external_scanner.searching_state')}
      </Text>
    </View>
  ) : (
    <KeyboardAwareRecyclerList
      alwaysBounceVertical={false}
      estimatedItemSize={80}
      extraData={quantityMap}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
      listItemSeparatorProps={{
        style: styles.itemSeparator,
      }}
      renderItem={({ item }) => (
        <View style={styles.itemWrapper}>
          <ScannerVariantPackListItem
            option={adaptVariantPackToItemListOption(item)}
            style={styles.searchResult}
          />
          <QuantitySelector
            isFocused={false}
            onChange={(_, quantity) => {
              onAddItemToBatch(item.uuid, quantity);
            }}
            quantity={quantityMap[item.uuid] ?? 0}
          />
        </View>
      )}
      renderSectionHeader={(item) => (
        <View style={styles.sectionHeader}>
          <Text style={[TYPOGRAPHY.listTitle, styles.sectionHeaderText]}>
            {item.title}
          </Text>
        </View>
      )}
      sections={sections}
      stickyHeaderIndices={[]}
    />
  );
};

const useStyles = createStyles((theme) => ({
  itemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
  itemWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  searchLoading: {
    alignItems: 'center',
    flex: 1,
    gap: 16,
    justifyContent: 'center',
  },
  searchResult: {
    flex: 1,
    marginVertical: 16,
  },
  searchText: {
    color: theme.sk.color.text.secondary,
  },
  sectionHeader: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  sectionHeaderText: {
    color: theme.sk.color.text.secondary,
  },
}));
